import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { post } from "../Helper/http";
import md5 from 'md5';
import "./CartSummary.scss";

type CartItem = {
  id: number;
  imgSrc: string;
  title: string;
  price: string;
  badge?: string;
  quantity: number;
};

type CustomerForm = {
  name: string;
  phone: string;
  address: string;
};

function randomstring(length: number): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";

  for (let i = 0; i < length; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
}

function CartSummary() {
  useEffect(() => {
    document.title = "INTER COMM | Cart Summary";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();

  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    const saved = localStorage.getItem("cartItems");
    return saved ? JSON.parse(saved) : [];
  });

  const [customer, setCustomer] = useState<CustomerForm>({
    name: "",
    phone: "",
    address: "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!message) return;

    const timer = setTimeout(() => {
      setMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [message]);


  const getNumericPrice = (price: string) => Number(price.replace(/[^0-9.]/g, ""));

  const subtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      return sum + getNumericPrice(item.price) * item.quantity;
    }, 0);
  }, [cartItems]);

  const totalItems = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  }, [cartItems]);

  const updateCart = (updated: CartItem[]) => {
    setCartItems(updated);
    localStorage.setItem("cartItems", JSON.stringify(updated));
  };

  const handleIncrease = (id: number) => {
    const updated = cartItems.map((item) =>
      item.id === id ? { ...item, quantity: item.quantity + 1 } : item
    );
    updateCart(updated);
  };

  const handleDecrease = (id: number) => {
    const updated = cartItems
      .map((item) =>
        item.id === id ? { ...item, quantity: item.quantity - 1 } : item
      )
      .filter((item) => item.quantity > 0);

    updateCart(updated);
  };

  const handleRemove = (id: number) => {
    const updated = cartItems.filter((item) => item.id !== id);
    updateCart(updated);
  };

  const handleCustomerChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCustomer((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!customer.name.trim()) return "Please enter customer name.";
    if (!customer.phone.trim()) return "Please enter phone number.";
    if (!customer.address.trim()) return "Please enter address.";
    if (cartItems.length === 0) return "Your cart is empty.";
    return "";
  };

  const handleProceedToPayment = async () => {
    const validationError = validateForm();
    if (validationError) {
      setMessage(validationError);
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const merchantId = "cl885dat1000009kzfbe67iwn";
      const countryId = "MY";
      const ip = "1.1.1.1";
      const notificationUrl = "http://intercommai.com";
      const redirectUrl = "http://intercommai.com";
      const referenceId = "DEP" + randomstring(10);

      const rawSign = `amount=${subtotal}&apiKey=cl0s5x35m0001b0uqbs6tg30t&countryId=${countryId}&ip=${ip}&merchantId=${merchantId}&notificationUrl=${notificationUrl}&referenceId=${referenceId}&cl0s5x35n0002b0uqhf1mbdi4`;
      const sign = md5(rawSign);

      const payload = {
        // customer: {
        //   name: customer.name.trim(),
        //   phone: customer.phone.trim(),
        //   address: customer.address.trim(),
        // },
        // items: cartItems.map((item) => ({
        //   productId: item.id,
        //   title: item.title,
        //   price: getNumericPrice(item.price),
        //   quantity: item.quantity,
        //   lineTotal: getNumericPrice(item.price) * item.quantity,
        // })),
        merchantId,
        countryId,
        ip,
        amount: subtotal,
        referenceId,
        notificationUrl,
        redirectUrl,
        playerName: customer.name.trim(),
        payType: "1001",
        sign,
      };

      console.log(payload);

      const resp = await post(
        "https://api.dreamkpay.com/request-deposit",
        JSON.stringify(payload)
      );

      if (resp.status >= 200 && resp.status < 300) {
        const result =
          typeof resp.data === "string" ? JSON.parse(resp.data) : resp.data;

        if (result?.success === true && typeof result?.data === "string") {
          setMessage("Redirecting to payment...");

          setTimeout(() => {
            window.location.href = result.data;
          }, 500);
        } else {
          setMessage("Payment Gateway offline. Kindly Contact Administrator.");
          console.log("Unexpected response:", result);
        }
      } else {
        setMessage("Failed to proceed payment.");
      }
    } catch (error) {
      console.error(error);
      setMessage("Unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="cart-summary-page">
      <div className="container">
        <div className="cart-summary-topbar">
          <div>
            <span className="cart-summary-kicker">SHOPPING CART</span>
            <h1>Order Summary</h1>
            <p>{totalItems} item(s) in your cart</p>
          </div>

          <button
            type="button"
            className="cart-back-btn"
            onClick={() => navigate("/")}
          >
            <i className="fa-regular fa-cart-shopping-fast fa-icon"></i>Continue Shopping
          </button>
        </div>

        {cartItems.length === 0 ? (
          <div className="cart-empty-state">
            <div className="cart-empty-icon">
              <i className="fa-solid fa-cart-shopping-fast" aria-hidden="true"></i>
            </div>
            <h2>Your cart is empty</h2>
            <p>Browse products and add items into your cart.</p>
            <button
              type="button"
              className="primary-btn"
              onClick={() => navigate("/")}
            >
              Back to Store
            </button>
          </div>
        ) : (
          <div className="cart-summary-layout">
            <div className="cart-summary-main">
              <div className="customer-info-card">
                <h2>Customer Information</h2>

                <div className="customer-form-grid">
                  <div className="form-field full-width">
                    <label htmlFor="customer-name">Full Name</label>
                    <input
                      id="customer-name"
                      name="name"
                      type="text"
                      value={customer.name}
                      onChange={handleCustomerChange}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="customer-phone">Phone Number</label>
                    <input
                      id="customer-phone"
                      name="phone"
                      type="text"
                      value={customer.phone}
                      onChange={handleCustomerChange}
                      placeholder="Example: 01112063712"
                    />
                  </div>

                  <div className="form-field full-width">
                    <label htmlFor="customer-address">Address</label>
                    <textarea
                      id="customer-address"
                      name="address"
                      value={customer.address}
                      onChange={handleCustomerChange}
                      placeholder="Enter delivery / billing address"
                      rows={4}
                    />
                  </div>
                </div>
              </div>

              <div className="cart-summary-list">
                {cartItems.map((item) => {
                  const unitPrice = getNumericPrice(item.price);
                  const lineTotal = unitPrice * item.quantity;

                  return (
                    <article className="cart-item-card" key={item.id}>
                      <div className="cart-item-image">
                        <img src={item.imgSrc} alt={item.title} />
                      </div>

                      <div className="cart-item-details">
                        <h3 title={item.title}>{item.title}</h3>
                        <div className="cart-item-meta">
                          <span className="cart-item-unit-price">{item.price}</span>
                        </div>

                        <div className="cart-item-controls">
                          <div className="qty-box">
                            <button type="button" onClick={() => handleDecrease(item.id)}>
                              -
                            </button>
                            <span>{item.quantity}</span>
                            <button type="button" onClick={() => handleIncrease(item.id)}>
                              +
                            </button>
                          </div>

                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => handleRemove(item.id)}
                          >
                            <i className="fa-duotone fa-regular fa-trash-can"></i>
                          </button>
                        </div>
                      </div>

                      <div className="cart-item-total">
                        <span className="cart-item-total-label">Total</span>
                        <strong>RM {lineTotal.toLocaleString()}</strong>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <aside className="cart-summary-sidebar">
              <div className="summary-card">
                <h2>Payment Summary</h2>

                <div className="summary-row">
                  <span>Items</span>
                  <span>{totalItems}</span>
                </div>

                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>RM {subtotal.toLocaleString()}</span>
                </div>

                <div className="summary-row total">
                  <span>Total</span>
                  <span>RM {subtotal.toLocaleString()}</span>
                </div>

                <button
                  type="button"
                  className="primary-btn summary-pay-btn"
                  onClick={handleProceedToPayment}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Proceed to Payment"}
                </button>

                {message && <p className="summary-message"><i className="fa-regular fa-seal-exclamation fa-icon"></i>{message}</p>}
              </div>
            </aside>
          </div>
        )}
      </div>
    </section>
  );
}

export default CartSummary;